/**
 * Auth store — current user, current team, login/logout flow.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '@/services/api';
import { i18n } from '@/locales';

// Locale-aware error message helper. Resolves at call time so messages follow
// the user's current i18n locale.
function tErr(key: string): string {
  return i18n.global.t(key) as string;
}

export interface CurrentUser {
  id: string;
  email: string;
  full_name: string | null;
  locale: string;
  is_admin: boolean;
  /**
   * Platform tier (migration 0054) — WHO you are on the platform, which is a
   * different axis from `team.role` (what you may do inside one workspace).
   * `is_admin` above is the legacy boolean and stays true for superadmin+dev;
   * anything that needs to tell those two apart, or to see a distributor or a
   * reseller, must read this.
   */
  platform_tier?: 'superadmin' | 'dev' | 'distributor' | 'reseller' | 'none';
  is_demo?: boolean;                  // shared read-only demo workspace
  email_verified?: boolean;
  sso_provider?: string | null;       // 'google' | 'microsoft' | null
  terms_accepted_at?: string | null;  // ISO datetime
  terms_version?: string | null;      // e.g. '2026-04-30'
}

export interface CurrentTeam {
  id: string;
  slug: string;
  name: string;
  plan_code: string;     // legacy / informational only since pricing v2.0
  status?: string;
  role: string;
  paid_bot_count?: number; // for Partner badge (5+ qualifies)
  is_platform_root?: boolean; // top of the company tree — no Subscription page
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const team = ref<CurrentTeam | null>(null);
  const teams = ref<CurrentTeam[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.is_admin === true);
  const isDemo = computed(() => user.value?.is_demo === true);
  const tier = computed(() => user.value?.platform_tier ?? 'none');
  /** Runs a tenant: a white-label distributor, or Vistoweb itself. */
  const isDistributor = computed(() => tier.value === 'distributor' || tier.value === 'superadmin');
  const isReseller = computed(() => tier.value === 'reseller');

  async function login(email: string, password: string) {
    loading.value = true; error.value = null;
    try {
      const data = await authApi.login(email, password);
      authApi.setTokens(data.access_token, data.refresh_token);
      user.value = data.user;
      team.value = data.user.current_team;
      return true;
    } catch (e: any) {
      const msg = e?.response?.data?.error;
      error.value = msg === 'invalid_credentials'
        ? tErr('auth.login.errors.invalid')
        : (e?.response?.data?.message || e?.message || tErr('auth.login.errors.generic'));
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function register(input: { email: string; password: string; full_name: string; team_name?: string; locale?: string; accept_terms: true; invite_token?: string; plan?: string }) {
    loading.value = true; error.value = null;
    try {
      const data = await authApi.register(input);
      authApi.setTokens(data.access_token, data.refresh_token);
      user.value = data.user;
      team.value = data.user.current_team;
      // Refresh /me to load proper team metadata (slug, paid_bot_count etc.)
      // — registration response stubs the slug as ''.
      await fetchMe();
      return true;
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === 'email_exists') {
        error.value = tErr('auth.signup.errors.emailExists');
      } else if (code === 'disposable_email') {
        error.value = tErr('auth.signup.errors.disposableEmail');
      } else if (code === 'rate_limited' || e?.response?.status === 429) {
        error.value = tErr('auth.signup.errors.rateLimit');
      } else if (code === 'validation_error' || e?.response?.status === 400) {
        error.value = tErr('auth.signup.errors.validation');
      } else {
        error.value = e?.response?.data?.message || e?.message || tErr('auth.signup.errors.generic');
      }
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMe() {
    if (!authApi.hasTokens()) {
      user.value = null; team.value = null; teams.value = [];
      return false;
    }
    try {
      const data = await authApi.me();
      user.value = data.user;
      teams.value = data.teams || [];
      team.value = teams.value.find((t) => t.id === data.current_team_id) ?? teams.value[0] ?? null;
      return true;
    } catch {
      user.value = null; team.value = null; teams.value = [];
      return false;
    }
  }

  async function logout() {
    await authApi.logout();
    authApi.clearTokens();
    user.value = null; team.value = null; teams.value = [];
  }

  // Password-less entry to the shared read-only demo workspace. Triggered by
  // the /demo route when a marketing-site visitor clicks "Try the dashboard".
  // The returned user has is_demo=1 — the API blocks all mutations server-side
  // with 403 demo_readonly, surfaced as a friendly modal in App.vue.
  async function demoLogin(locale: 'en' | 'el') {
    loading.value = true; error.value = null;
    try {
      const data = await authApi.demoLogin(locale);
      authApi.setTokens(data.access_token, data.refresh_token);
      user.value = data.user;
      team.value = data.user.current_team;
      // Pull /me to populate teams[] with paid_bot_count etc.
      await fetchMe();
      return true;
    } catch (e: any) {
      error.value = e?.response?.data?.message || e?.message || 'Demo unavailable';
      return false;
    } finally {
      loading.value = false;
    }
  }

  function setError(msg: string | null) { error.value = msg; }

  // Listen to forced logout from axios interceptor
  if (typeof window !== 'undefined') {
    window.addEventListener('vc:auth-required', () => {
      user.value = null; team.value = null; teams.value = [];
    });
  }

  return {
    user, team, teams, loading, error,
    isAuthenticated, isAdmin, isDemo, tier, isDistributor, isReseller,
    login, register, demoLogin, logout, fetchMe, setError,
  };
});
